import { useEffect, useRef, useState } from 'react';

import { profileHref } from '../auth/profile';
import { useConfig } from '../query/releases';

interface UserMenuProps {
  label: string;
  onSignOut: () => void;
}

/**
 * The signed-in user's menu: their name opens it. "Your profile" leaves for
 * SkyMap Id's profile page in the top window (the page refuses framing) and
 * returns to the page the user was on; the tokens stay in this tab's
 * sessionStorage meanwhile, so they come back signed in.
 */
export default function UserMenu({ label, onSignOut }: UserMenuProps) {
  const { data: config } = useConfig();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const profileUrl = config?.profileUrl;

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className="user-menu-trigger"
        type="button"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="nav-email">{label}</span>
        <span aria-hidden="true" className="user-menu-caret">
          ▾
        </span>
      </button>
      {open && (
        <div className="user-menu-list" role="menu">
          {profileUrl && (
            <a
              className="user-menu-item"
              href={profileHref(profileUrl, window.location.href)}
              role="menuitem"
            >
              Your profile
            </a>
          )}
          <button
            className="user-menu-item"
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
